import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Form, Button, message } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useGenericMutation } from "../../../queries/use-generic-mutation";
import sidebarImage from "../../../assets/login-sidebar.webp";
import { showUserNotification } from "../../../utils/functions";
import SOURCES from "../../../utils/constants";
import CommonForm from "../../global/form";
import Spinner from "../../global/spinner";

const EnhancedVerifyPassword = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // Get params from URL
    const queryParams = new URLSearchParams(window.location.search);
    const paramEmail = queryParams.get("email");
    const paramCode = queryParams.get("code");

    const doPasswordVerifyMutation = useGenericMutation({
        endpoint: `/user/forgot-password-verify`,
    });

    const handleFinish = async (values) => {
        setLoading(true);
        try {
            const result = await doPasswordVerifyMutation.mutateAsync({
                verificationCode: values.code,
                email: values.email,
                new_password: values.newPassword,
                confirm_password: values.confirmPassword,
            });

            if (result) {
                message.success("Password has been changed");
                setTimeout(() => {
                    history.push("/");
                }, 2000);
            } else {
                setLoading(false);
            }
        } catch (err) {
            setLoading(false);
            if (err.response && err.response.data) {
                message.error(err.response.data.error || err.response.data.message || "An error occurred");
            }
        }
    };

    const passwordValidator = (_, value) => {
        if (!value) return Promise.resolve();

        const hasUpperCase = /[A-Z]/.test(value);
        const hasNumber = /[0-9]/.test(value);
        const isLongEnough = value.length >= 8;
        const hasSpecialChar = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(value);
        const hasSpace = value.includes(" ");

        if (!hasUpperCase || !hasNumber || !isLongEnough) {
            return Promise.reject(new Error("Must include (1) Capital Letter, (1) Numeric Digit, Minimum Length of (8)."));
        }
        if (hasSpecialChar) {
            return Promise.reject(new Error("Must not include Special Characters."));
        }
        if (hasSpace) {
            return Promise.reject(new Error("Must not include spaces."));
        }

        return Promise.resolve();
    };

    const formData = [
        {
            label: "Verification Code",
            name: "code",
            type: "text",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "Check email for Verification Code",
            initialValue: paramCode,
            span: 24,
            message: "Verification Code is required",
        },
        {
            label: "Email",
            name: "email",
            type: "email",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "Email",
            initialValue: paramEmail,
            span: 24,
            readOnly: true,
            message: "Email is required",
        },
        {
            label: "New Password",
            name: "newPassword",
            type: "password",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "New password",
            span: 24,
            message: "New Password is required",
            rules: [{ validator: passwordValidator }]
        },
        {
            label: "Confirm Password",
            name: "confirmPassword",
            type: "password",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "Confirm password",
            span: 24,
            message: "Confirm Password is required",
            dependencies: ['newPassword'],
            rules: [
                ({ getFieldValue }) => ({
                    validator(_, value) {
                        console.log(value, getFieldValue('newPassword'));
                        if (!value || getFieldValue('newPassword') === value) {
                            return Promise.resolve();
                        }
                        return Promise.reject(new Error('The two passwords that you entered do not match!'));
                    },
                }),
            ],
        },
    ];

    return (
        <div className="flex min-h-screen">
            {/* Left Side - Image & Slogan */}
            <div className="hidden lg:flex w-1/2 relative bg-gray-900 items-center justify-center overflow-hidden">
                <img
                    src={sidebarImage}
                    alt="Background"
                    className="absolute inset-0 w-full h-full object-cover"
                />

                {/* Logo on Sidebar */}
                <div className="absolute top-8 left-8 z-30">
                    <h1 className="text-4xl font-bold tracking-tighter m-0">
                        <span className="text-primary">Leads</span>
                        <span className="text-white">Import</span>
                    </h1>
                </div>
            </div>

            {/* Right Side - Verify Password Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center bg-white p-8 lg:p-16 overflow-y-auto max-h-screen">
                <div className="w-full max-w-md space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <div className="text-center">
                        <h2 className="text-3xl font-bold text-gray-900">Change Password</h2>
                        <p className="mt-2 text-gray-500">Enter your new password details</p>
                    </div>

                    <div className="bg-white">
                        <CommonForm
                            form={form}
                            data={formData}
                            onFinish={handleFinish}
                            isInternalSubmitt={false}
                        />

                        <Button
                            type="primary"
                            onClick={() => form.submit()}
                            className="w-full h-12 text-base font-bold rounded-xl shadow-lg shadow-primary/25 !bg-primary hover:!bg-primary/90 border-none mt-4 flex items-center justify-center gap-x-2"
                        >
                            {loading && <Spinner className="!border-white " />}
                            {!loading && <LockOutlined className="text-lg" />}
                            Change Password
                        </Button>

                        <p className="text-center mt-8 text-gray-600">
                            Remember your password?{" "}
                            <Link to="/" className="font-bold text-primary hover:text-primary/80">
                                Go to Sign In
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnhancedVerifyPassword;
